import { useEffect, useState } from 'react';
import styles from './Reviews.module.css';
import Header from './components/Header';
import SideBar from './components/SideBar';
import LoadMoreButton from './components/LoadMoreButton';
import useReviewQueries from './hooks/useReviewQueries';
import { fmtTime, fmtBytes, elapsed } from './utils/reviewFormat';
import {
  COUNTRIES, SITE_LABELS, DEFAULT_COUNTRY, DEFAULT_SITE,
  countrySites, isCountrySelectable, firstEnabledSite,
} from './utils/reviewCountries';

const API = '/api/reviews';
const EN_TIME = { s: 's', m: 'm ' };

const STATUS = {
  0: { text: 'In progress', className: styles.statusProgress },
  1: { text: 'Done', className: styles.statusDone },
  2: { text: 'Failed', className: styles.statusFailed },
};

const ERROR_TEXT = {
  keyword_required: 'Enter a product to search.',
  keyword_too_long: 'Search term is too long (max 255 characters).',
  invalid_site: 'Select a web site.',
  site_disabled: 'This web site is not available yet.',
  active_query_exists: 'You already have a collection in progress. Try again when it finishes.',
  s3_not_configured: 'Storage is not configured on the server.',
};

const EN_MESSAGES = {
  errorText: (data) => {
    if (ERROR_TEXT[data.error]) return ERROR_TEXT[data.error];
    if (data.error === 'trigger_failed') return `Failed to start collection: ${data.message || ''}`;
    return data.message || data.error || 'Request failed';
  },
  reused: 'This search was already collected — showing the previous result.',
  joined: 'Someone is already collecting this search — you joined it.',
  started: 'Collection started.',
  downloadFailed: 'Download failed',
};

const flagUrl = (code) => `https://flagcdn.com/w40/${code}.png`;
const siteLabel = (key) => SITE_LABELS[key] || key;

function StatusBadge({ status }) {
  const s = STATUS[status] || { text: String(status), className: '' };
  return <span className={s.className}>{s.text}</span>;
}

function Section({ label, children }) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionTab}>{label}</div>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}

export default function Reviews() {
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [site, setSite] = useState(DEFAULT_SITE);
  const [keyword, setKeyword] = useState('');
  const {
    config, active, selected, history, cursor, submitting, downloading, notice,
    busy, submit: send, selectQuery, loadMore, download,
  } = useReviewQueries(API, EN_MESSAGES);

  const sites = countrySites(country, config?.sites);

  // config 로드 후 현재 사이트가 선택 국가에서 비활성이면 그 나라의 첫 활성 사이트로
  useEffect(() => {
    if (!config) return;
    setSite(cur => countrySites(country, config.sites).find(s => s.key === cur)?.enabled
      ? cur : firstEnabledSite(country, config.sites));
  }, [config]); // eslint-disable-line react-hooks/exhaustive-deps -- config 도착 시 1회만 보정

  const pickCountry = (code) => {
    if (busy || code === country || !isCountrySelectable(code)) return;
    setCountry(code);
    setSite(firstEnabledSite(code, config?.sites));
  };

  const pickSite = (s) => {
    if (busy || !s.enabled) return;
    setSite(s.key);
  };

  const siteEnabled = !!sites.find(s => s.key === site)?.enabled;
  const canSubmit = !busy && siteEnabled && keyword.trim() !== '';

  const collect = () => {
    if (!canSubmit) return;
    send({ site, keyword: keyword.trim(), force: false });
  };

  const recollect = () => {
    if (!selected || busy) return;
    send({ site: selected.site, keyword: selected.keyword, force: true });
  };

  const canDownload = selected?.status === 1 && !downloading;

  return (
    <div className={styles.page}>
      <Header />
      <div className={styles.mainContent}>
        <SideBar />
        <div className={styles.body}>
          <Section label="Search">
            <div className={styles.formRow}>
              <div className={styles.rowLabel}>Country:</div>
              <div className={styles.options}>
                {COUNTRIES.map(c => {
                  const selectable = isCountrySelectable(c.code);
                  return (
                    <label key={c.code} className={`${styles.option} ${selectable ? '' : styles.optionDisabled}`}>
                      <span className={styles.optionHead}>
                        <img className={styles.flag} src={flagUrl(c.code)} alt="" />
                        <span>{c.label}</span>
                      </span>
                      <input type="checkbox" checked={country === c.code} disabled={!selectable || busy}
                        onChange={() => pickCountry(c.code)} />
                      {!selectable && <span className={styles.optionNote}>Coming soon</span>}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.rowLabel}>Web Site:</div>
              <div className={styles.options}>
                {sites.map(s => (
                  <label key={s.key} className={`${styles.option} ${s.enabled ? '' : styles.optionDisabled}`}>
                    <span className={`${styles.optionHead} ${styles.wordmark} ${styles[`wordmark_${s.key}`]}`}>{s.label}</span>
                    <input type="checkbox" checked={site === s.key} disabled={!s.enabled || busy}
                      onChange={() => pickSite(s)} />
                    {!s.enabled && <span className={styles.optionNote}>Not available</span>}
                  </label>
                ))}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.rowLabel}>Search<br />Product:</div>
              <input className={styles.searchInput} value={keyword} disabled={busy}
                onChange={e => setKeyword(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') collect(); }} />
              <button className={styles.primaryBtn} onClick={collect} disabled={!canSubmit}>
                {submitting ? 'Requesting...' : active ? 'Collecting...' : 'Collect Reviews'}
              </button>
            </div>
            {config && (
              <div className={styles.hint}>
                Search Limit: {config.max_products} products; Same search will use previous search request
              </div>
            )}
            {notice && <div className={notice.type === 'error' ? styles.noticeError : styles.noticeInfo}>{notice.text}</div>}

            {active && (
              <div className={styles.activeCard}>
                <div className={styles.activeTitle}>{siteLabel(active.site)} · {active.keyword}</div>
                <div className={styles.meta}>
                  <StatusBadge status={active.status} /> · {elapsed(active.created, null, EN_TIME)} elapsed
                </div>
                <div className={styles.hint}>Collection continues on the server even if you leave this page. The result will appear here when it finishes.</div>
              </div>
            )}
          </Section>

          {selected && (
            <Section label="Results">
              <div className={styles.resultHead}>
                <div className={styles.resultTitle}>
                  {siteLabel(selected.site)} · {selected.keyword} <span className={styles.muted}>#{selected.id}</span>
                </div>
                <div className={styles.buttons}>
                  <button className={styles.greenBtn} onClick={() => download('json', selected.keyword)} disabled={!canDownload}>JSON</button>
                  <button className={styles.greenBtn} onClick={() => download('csv', selected.keyword)} disabled={!canDownload}>CSV</button>
                  <button className={styles.redBtn} onClick={recollect} disabled={busy}>Re-collect</button>
                  <button className={styles.primaryBtn} disabled title="Coming soon">Analysis</button>
                </div>
              </div>
              <div className={styles.meta}>
                <StatusBadge status={selected.status} />
                <span>Products {selected.product_count ?? '-'}</span>
                <span>Reviews {selected.review_count ?? '-'}</span>
                <span>Failed records {selected.error_count ?? '-'}</span>
                <span>Size {fmtBytes(selected.size_bytes)}</span>
                <span>Duration {elapsed(selected.created, selected.finished, EN_TIME)}</span>
                <span>Finished {fmtTime(selected.finished)}</span>
              </div>
              {selected.error && <div className={styles.noticeError}>Error: {selected.error}</div>}
              {Array.isArray(selected.summary) && selected.summary.length > 0 && (
                <table className={styles.table}>
                  <thead><tr>
                    <th>Rank</th><th>Product</th><th>Brand</th><th>Price</th>
                    <th>Rating</th><th>Reviews / Collected</th><th>Status</th>
                  </tr></thead>
                  <tbody>
                    {selected.summary.map((p, i) => (
                      <tr key={`${p.id || 'x'}-${i}`}>
                        <td>{p.search_rank ?? '-'}</td>
                        <td>{p.url ? <a href={p.url} target="_blank" rel="noreferrer">{p.name || p.id || '-'}</a> : (p.name || p.id || '-')}</td>
                        <td>{p.brand || '-'}</td>
                        <td>{p.price_text || '-'}</td>
                        <td>{p.rating ?? '-'}</td>
                        <td>{p.review_count ?? '-'} / {p.reviews_collected}</td>
                        <td className={p.error_code ? styles.statusFailed : styles.statusDone}>{p.error_code || 'ok'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {selected.status === 1 && (!selected.summary || selected.summary.length === 0) && (
                <div className={styles.hint}>No products were collected.</div>
              )}
            </Section>
          )}

          <Section label="Request History">
            <table className={styles.table}>
              <thead><tr>
                <th>#</th><th>Site</th><th>Keyword</th><th>Status</th><th>Products / Reviews</th><th>Requested at</th>
              </tr></thead>
              <tbody>
                {history.map(q => (
                  <tr key={q.id} onClick={() => selectQuery(q.id)}
                    className={`${styles.clickableRow} ${selected?.id === q.id ? styles.selectedRow : ''}`}>
                    <td>{q.id}</td>
                    <td>{siteLabel(q.site)}</td>
                    <td>{q.keyword}</td>
                    <td><StatusBadge status={q.status} /></td>
                    <td>{q.product_count ?? '-'} / {q.review_count ?? '-'}</td>
                    <td>{fmtTime(q.created)}</td>
                  </tr>
                ))}
                {history.length === 0 && <tr><td colSpan={6} className={styles.muted}>No requests yet.</td></tr>}
              </tbody>
            </table>
            <LoadMoreButton hasMore={!!cursor} onClick={loadMore} label="Load more" />
          </Section>
        </div>
      </div>
    </div>
  );
}
